/**
 * Per-Section Dispatch Hub — Phase B scaffold.
 *
 * Renders the same tile layout as the legacy Dispatch dashboard but
 * scoped to a single catalog section (color_combo / art_silk / kids).
 * Each tile routes to a per-section URL like
 *   /sections/dispatch/color_combo/inward-consignment
 *
 * In Phase B the sub-screens are STUBS (`<DispatchSectionStub />`).
 * Phase C wires the Color Combo flows end-to-end with per-section
 * MongoDB collections; Phases D + E replicate to Art Silk + Kids.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, Inbox, Send, ArrowLeftRight, Package, FileText, BarChart3,
  Users, Warehouse, Box, ClipboardCheck, MapPin, AlertTriangle, Truck, Layers, Undo2, RotateCcw, Smartphone, Settings, Shuffle, ListChecks, Layout,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import apiClient from '@/services/apiClient';
import { filterOperations } from '@/utils/operationAccess';

// Static catalog mirror — keeps the hub functional even when offline.
// Backend `/api/dispatch-sections/catalog` is the authoritative source.
const FALLBACK_CATALOG = {
  color_combo: { display_name: 'Color Combo Section', tagline: 'Color combos picking, packing, dispatch' },
  art_silk:    { display_name: 'Art Silk Section',    tagline: 'Art silk SFG/FG storage + dispatch' },
  kids:        { display_name: 'Kids Section',        tagline: 'Kids SFG/FG storage + dispatch' },
};

export default function PerSectionDispatchHub() {
  const navigate = useNavigate();
  const { section: slug } = useParams();
  // 2026-05-27 — Render the page IMMEDIATELY using FALLBACK_CATALOG;
  // the two API calls (catalog + enabled) run in the background just to
  // refine the display_name + flip the "section not enabled" amber
  // banner. Previously we blocked behind a full-page <Loader2 /> until
  // BOTH (sequential) `await apiClient.get(...)` calls returned, which
  // on slow networks / SSE-noisy proxies stretched first paint to ~20s
  // on the Srivilliputhur site (user-reported). The fallback already
  // carries everything we need to render the three tile grids, so
  // first paint is now O(0ms).
  const [section, setSection] = useState(FALLBACK_CATALOG[slug] || null);
  const [allowed, setAllowed] = useState(true);   // optimistic — flip false if backend says otherwise

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const siteId = (typeof localStorage !== 'undefined' && localStorage.getItem('active_site_id')) || 'salem';
        // Run BOTH lookups in parallel — they're independent.
        const [c, e] = await Promise.all([
          apiClient.get('/dispatch-sections/catalog'),
          apiClient.get(`/dispatch-sections/sites/${siteId}/enabled`),
        ]);
        if (cancelled) return;
        const match = (c.data?.sections || []).find((s) => s.slug === slug);
        if (match) setSection({ display_name: match.display_name, tagline: match.description });
        const enabled = e.data?.enabled || [];
        setAllowed(enabled.includes(slug));
      } catch (_err) {
        // Fail-soft: keep the optimistic-fallback render, hide the
        // amber banner since we can't verify. Operator can still drill
        // into tiles even if the catalog endpoint hiccups.
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  const basePath = `/sections/dispatch/${slug}`;

  const wmsTiles = [
    { id: 'traffic-control',      title: 'Traffic Control',     description: 'Monitor & manage warehouse row reservations', icon: MapPin,         iconBg: 'bg-orange-500',  route: `${basePath}/traffic-control` },
    { id: 'dispatch-supervisor',  title: 'Dispatch Supervisor', description: 'Manage pickers & auto-allocate pick slips',   icon: Users,          iconBg: 'bg-indigo-500',  route: `${basePath}/dispatch-supervisor` },
    { id: 'sfg-put-away',         title: 'SFG Put-Away',        description: 'Store received bundles into bins',             icon: Package,        iconBg: 'bg-fuchsia-500', route: `${basePath}/sfg-put-away` },
    { id: 'sfg-pick',             title: 'SFG Pick',            description: 'Pick stored bundles from bins',                icon: Truck,          iconBg: 'bg-rose-500',    route: `${basePath}/sfg-pick` },
    { id: 'fg-put-away',          title: 'FG Put-Away',         description: 'Store finished packs into FG bins',            icon: Box,            iconBg: 'bg-amber-500',   route: `${basePath}/fg-put-away` },
    { id: 'fg-pick',              title: 'FG Pick',             description: 'Pick stored packs from FG bins',               icon: Truck,          iconBg: 'bg-pink-600',    route: `${basePath}/fg-pick` },
    { id: 'combo-v2',             title: 'ComboV2 Plans',       description: 'Plan & distribute combo runs to PP tables',    icon: Layers,         iconBg: 'bg-fuchsia-600', route: `${basePath}/combo-v2` },
    { id: 'pp-station',           title: 'PP Station',          description: 'Scan USNs to assemble combos at PP tables',    icon: ClipboardCheck, iconBg: 'bg-cyan-600',    route: `${basePath}/pp-station` },
    { id: 'return-excess',        title: 'Return Excess',       description: 'Wrap leftover USNs back into PUT-AWAY bundles', icon: Undo2,         iconBg: 'bg-orange-600',  route: `${basePath}/return-excess` },
    { id: 'combo-reversal',       title: 'Combo Reversal',      description: 'Undo assembled combos & re-queue components',   icon: RotateCcw,     iconBg: 'bg-rose-600',    route: `${basePath}/combo-reversal` },
    { id: 'mobile-dashboard',     title: 'Mobile Dashboard',    description: 'Open the touch-optimised WMS Mobile UI',        icon: Smartphone,    iconBg: 'bg-emerald-700', route: `${basePath}/mobile` },
    { id: 'sfg-wms',              title: 'SFG WMS',             description: `Semi-Finished Goods · ${slug}`,                icon: Warehouse,      iconBg: 'bg-purple-500',  route: `${basePath}/sfg-wms` },
    { id: 'fg-wms',               title: 'FG WMS',              description: `Finished Goods · ${slug}`,                     icon: Box,            iconBg: 'bg-emerald-500', route: `${basePath}/fg-wms` },
  ];

  const setupTiles = [
    { id: 'location-setup',          title: 'Location Setup',        description: 'Define rows, racks, levels, bin codes',         icon: Layout,        iconBg: 'bg-slate-600',  route: `${basePath}/wms-setup/location-setup` },
    { id: 'zone-mapping',            title: 'Zone Mapping',          description: 'Velocity classes & ABC pick zones',             icon: MapPin,        iconBg: 'bg-indigo-600', route: `${basePath}/wms-setup/zone-mapping` },
    { id: 'wms-settings',            title: 'WMS Settings',          description: 'Replenishment thresholds & capacity rules',     icon: Settings,      iconBg: 'bg-slate-700',  route: `${basePath}/wms-setup/settings` },
    { id: 'footprint-capacity',      title: 'Footprint Capacity',    description: 'Per-design bin capacity overrides',             icon: BarChart3,     iconBg: 'bg-teal-600',   route: `${basePath}/wms-setup/footprint-capacity` },
    { id: 'sfg-bin-transfer',        title: 'SFG Bin-to-Bin',        description: 'Move SFG bundles between bins',                 icon: Shuffle,       iconBg: 'bg-purple-600', route: `${basePath}/wms-setup/sfg-bin-to-bin-transfer` },
    { id: 'fg-bin-transfer',         title: 'FG Bin-to-Bin',         description: 'Move FG packs between bins',                    icon: Shuffle,       iconBg: 'bg-emerald-600', route: `${basePath}/wms-setup/bin-to-bin-transfer` },
    { id: 'fg-bin-audit',            title: 'FG Bin Audit',          description: 'Physical FG bin counts vs system',              icon: ListChecks,    iconBg: 'bg-amber-600',  route: `${basePath}/wms-setup/fg-bin-audit` },
    { id: 're-sticker',              title: 'FG Re-Sticker Queue',   description: 'Bulk re-print FG stickers after price revision', icon: RotateCcw,    iconBg: 'bg-rose-600',   route: `${basePath}/re-sticker` },
  ];

  const sectionTiles = [
    { id: 'pieces-processed',          title: 'Pieces Processed',         description: 'Track staff work for wage calc',                icon: ClipboardCheck, iconBg: 'bg-green-500',   route: `${basePath}/pieces-processed` },
    { id: 'inward-consignment',        title: 'Inward Consignment',       description: 'Receive incoming consignments',                  icon: Inbox,          iconBg: 'bg-teal-500',    route: `${basePath}/inward-consignment` },
    { id: 'shipment-request',          title: 'Shipment Request',         description: 'View & process shipment requests',               icon: Send,           iconBg: 'bg-orange-500',  route: `${basePath}/shipment-request` },
    { id: 'internal-transfer',         title: 'Internal Transfers',       description: 'Transfers within / between sections',            icon: ArrowLeftRight, iconBg: 'bg-blue-500',    route: `${basePath}/internal-transfer` },
    { id: 'bundle-receipt',            title: 'Bundle Receipt Entry',     description: 'Process received bundles',                       icon: Package,        iconBg: 'bg-cyan-500',    route: `${basePath}/bundle-receipt` },
    { id: 'bundle-receipt-report',     title: 'Bundle Receipt Report',    description: 'All received bundles with details',              icon: FileText,       iconBg: 'bg-amber-500',   route: `${basePath}/bundle-receipt-report` },
    { id: 'day-wise-report',           title: 'Day Wise Transaction',     description: 'Daily summary report',                           icon: FileText,       iconBg: 'bg-slate-600',   route: `${basePath}/day-wise-report` },
    { id: 'closing-stock',             title: 'Closing Stock Report',     description: 'Live closing stock by operation',                icon: BarChart3,      iconBg: 'bg-teal-600',    route: `${basePath}/closing-stock` },
    { id: 'internal-transfer-report',  title: 'Internal Transfer Report', description: 'All received internal transfers',                icon: FileText,       iconBg: 'bg-indigo-600',  route: `${basePath}/internal-transfer-report` },
    { id: 'footprint-movement-report', title: 'Footprint Movement',       description: 'Put away & picking in FG/SFG storage',           icon: BarChart3,      iconBg: 'bg-cyan-600',    route: `${basePath}/footprint-movement-report` },
    { id: 'usn-tracking',              title: 'USN Tracking',             description: 'Complete USN movement history',                  icon: Package,        iconBg: 'bg-violet-600',  route: `${basePath}/usn-tracking` },
  ];

  const renderTile = (tile) => (
    <Card
      key={tile.id}
      className="hover:shadow-lg transition cursor-pointer border border-stone-200"
      onClick={() => navigate(tile.route)}
      data-testid={`tile-${slug}-${tile.id}`}
    >
      <CardContent className="p-4 flex items-start gap-3">
        <div className={`${tile.iconBg} text-white rounded-md p-2.5 flex-shrink-0`}>
          <tile.icon className="w-5 h-5" />
        </div>
        <div className="min-w-0">
          <div className="font-semibold text-stone-800 text-sm">{tile.title}</div>
          <div className="text-xs text-stone-500 mt-0.5 line-clamp-2">{tile.description}</div>
        </div>
      </CardContent>
    </Card>
  );

  if (!section) {
    return (
      <div className="min-h-screen p-8 bg-[#FFFBF5]">
        <div className="max-w-md mx-auto bg-white border border-rose-200 rounded-lg p-6 text-center">
          <AlertTriangle className="h-8 w-8 mx-auto text-rose-500 mb-2" />
          <h2 className="text-lg font-semibold text-stone-800">Unknown dispatch section</h2>
          <p className="text-sm text-stone-500 mt-1">No catalog entry for slug <code className="font-mono">{slug}</code>.</p>
          <Button className="mt-4" onClick={() => navigate('/admin/dashboard')}>Back</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FFFBF5]" data-testid={`per-section-hub-${slug}`}>
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/admin/dashboard')}
              className="text-[#F9FAFB] hover:bg-white/10"
              data-testid="back-button"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold font-display">{section.display_name}</h1>
              <p className="text-sm text-[#E8DCC8]">{section.tagline}</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-8">
        {!allowed && (
          <div className="mb-6 p-4 rounded-lg bg-amber-50 border border-amber-200 text-amber-900 text-sm flex gap-2">
            <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
            <div>
              <strong>This section is not enabled for the active site.</strong> A Super Admin
              must enable it in Site Management → Dispatch Sections before operators can use
              the live flows. (Tiles below are visible for review/scaffolding.)
            </div>
          </div>
        )}

        <div className="mb-8">
          <h2 className="text-2xl font-bold text-[#1A1F2E] font-display">Warehouse Management</h2>
          <p className="text-gray-600 mt-1">SFG &amp; FG Put-Away, Picking &amp; Allocation</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
          {filterOperations(`dispatch_${slug}`, wmsTiles).map(renderTile)}
        </div>

        <div className="mb-8">
          <h2 className="text-2xl font-bold text-[#1A1F2E] font-display">Section Operations</h2>
          <p className="text-gray-600 mt-1">Inward, transfers, packing, reports</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
          {filterOperations(`dispatch_${slug}`, sectionTiles).map(renderTile)}
        </div>

        <div className="mb-8">
          <h2 className="text-2xl font-bold text-[#1A1F2E] font-display">WMS Setup</h2>
          <p className="text-gray-600 mt-1">Section-scoped warehouse topology, settings, bin transfers &amp; audits</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {filterOperations(`dispatch_${slug}`, setupTiles).map(renderTile)}
        </div>
      </main>
    </div>
  );
}
